import React from 'react';
import { RadioButtonSimple, RadioButtonSimpleProps } from '@schuettflix/react-components';
import { Controller, ControllerProps, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LocationType } from '@/modules/location/domain/LocationSchema.ts';
import { cn } from '@/shared/utils/cn.ts';

export const WEIGHING_LOCATION_LABEL = {
    LOADING: 'forms.inputs.weighingLocation.loading.label',
    UNLOADING: 'forms.inputs.weighingLocation.unloading.label',
} as const satisfies Record<LocationType, string>;

type RadioButtonProps = Omit<RadioButtonSimpleProps, 'value' | 'onChange' | 'inputValue' | 'id' | 'name'> & {
    value: LocationType;
    inputValue: LocationType;
    onChange: React.Dispatch<React.SetStateAction<LocationType>>;
};

function WeighingLocationRadioButton({ value, inputValue, onChange, ...props }: RadioButtonProps) {
    return (
        <RadioButtonSimple
            value={value}
            inputValue={inputValue}
            id={`weighing-location-option-${inputValue}`}
            name="weighling-location"
            disabled={false}
            onChange={v => !!v && onChange(v as LocationType)}
            className="whitespace-nowrap"
            {...props}
        />
    );
}

type WeighingLocationSelectionInputProps = Partial<Pick<ControllerProps, 'defaultValue' | 'name'>> & {
    className?: string;
};

export const WeighingLocationSelectionInput = React.forwardRef<HTMLInputElement, WeighingLocationSelectionInputProps>(
    ({ name = 'weighingLocation', defaultValue = undefined, className }, _ref) => {
        const { control } = useFormContext();
        const { t } = useTranslation();

        return (
            <Controller
                control={control}
                defaultValue={defaultValue}
                name={name}
                render={({ field: { ref, value, onChange }, fieldState }) => (
                    <div
                        ref={ref}
                        tabIndex={-1}
                        className={cn(className, 'grid gap-2 py-2 focus:outline-none')}
                        data-test="weighing-location-selection-input"
                    >
                        <div className="flex flex-row gap-8">
                            <WeighingLocationRadioButton
                                value={value}
                                inputValue="LOADING"
                                label={t(WEIGHING_LOCATION_LABEL.LOADING)}
                                onChange={onChange}
                            />
                            <WeighingLocationRadioButton
                                value={value}
                                inputValue="UNLOADING"
                                label={t(WEIGHING_LOCATION_LABEL.UNLOADING)}
                                onChange={onChange}
                            />
                        </div>
                        {fieldState.error && (
                            <div className="text-critical font-copy-sm">{fieldState.error.message}</div>
                        )}
                    </div>
                )}
            />
        );
    }
);
